import type {ButtonProps, TextProps} from '@mantine/core';
import {
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import type {ReactNode} from 'react';

/**
 * Card of contextual actions — compact link-style actions by default, or a
 * full-width button for a single emphasized action.
 * @registry Card of contextual action buttons/links, optional danger variant.
 *
 * Ported from `library-m/layout/ActionCard`. Prop surface and compound API
 * (`ActionCard.Action`, `.Button`, `.Divider`, `.Description`, `.Empty`)
 * kept identical.
 *
 * Usage:
 * ```tsx
 * // Default compact style (link-style actions)
 * <ActionCard title="Actions">
 *   <ActionCard.Action icon={<IconPencil />} onClick={handleEdit}>
 *     Edit
 *   </ActionCard.Action>
 *   <ActionCard.Divider />
 *   <ActionCard.Action icon={<IconTrash />} onClick={handleDelete} color="red">
 *     Delete
 *   </ActionCard.Action>
 * </ActionCard>
 *
 * // With full-width button (for primary actions)
 * <ActionCard title="Admin Tools" icon={<IconSettings />} variant="danger">
 *   <ActionCard.Description>
 *     Use with caution. These actions cannot be undone.
 *   </ActionCard.Description>
 *   <ActionCard.Button onClick={handleForce} danger>
 *     Force Re-Process
 *   </ActionCard.Button>
 * </ActionCard>
 * ```
 */

export interface ActionCardProps {
  title?: string;
  icon?: ReactNode;
  children: ReactNode;
  /** Visual variant - danger shows red border */
  variant?: 'default' | 'danger';
}

export interface ActionCardActionProps {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  /** Color for the action (e.g., "red" for destructive) */
  color?: string;
  disabled?: boolean;
  loading?: boolean;
}

export interface ActionCardButtonProps extends Omit<ButtonProps, 'children'> {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  /** Shorthand for red color styling */
  danger?: boolean;
}

export interface ActionCardDescriptionProps
  extends Omit<TextProps, 'children'> {
  children: ReactNode;
}

export interface ActionCardEmptyProps {
  children: ReactNode;
}

// --- Sub-components ---

/** Compact link-style action (preferred for most actions) */
const ActionCardAction = ({
  children,
  icon,
  onClick,
  color = 'blue',
  disabled = false,
  loading = false,
}: ActionCardActionProps) => (
  <Button
    size="sm"
    w="fit-content"
    variant="subtle"
    onClick={onClick}
    disabled={disabled || loading}
    color={color}
    leftSection={
      icon && (
        <ThemeIcon size="sm" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      )
    }>
    {children}
  </Button>
);

/** Full-width button (for emphasized/primary actions) */
const ActionCardButton = ({
  children,
  icon,
  onClick,
  danger = false,
  color,
  variant = 'filled',
  ...props
}: ActionCardButtonProps) => (
  <Button
    size="md"
    fullWidth
    leftSection={icon}
    onClick={onClick}
    color={danger ? 'red' : color}
    variant={danger ? 'outline' : variant}
    {...props}>
    {children}
  </Button>
);

const ActionCardDivider = () => <Divider my="xs" />;

const ActionCardDescription = ({
  children,
  ...props
}: ActionCardDescriptionProps) => (
  <Text size="sm" c="dimmed" mb="sm" {...props}>
    {children}
  </Text>
);

const ActionCardEmpty = ({children}: ActionCardEmptyProps) => (
  <Text size="sm" c="dimmed" ta="center" py="xs">
    {children}
  </Text>
);

// --- Main Component ---

const ActionCardRoot = ({
  title = 'Actions',
  icon,
  children,
  variant = 'default',
}: ActionCardProps) => {
  const isDanger = variant === 'danger';

  return (
    <Card
      shadow="sm"
      withBorder
      p="md"
      style={
        isDanger ? {borderColor: 'var(--mantine-color-red-4)'} : undefined
      }>
      <Group gap="xs" mb="sm">
        {icon && (
          <ThemeIcon
            size="sm"
            variant="light"
            color={isDanger ? 'red' : 'gray'}>
            {icon}
          </ThemeIcon>
        )}
        <Title order={6} c={isDanger ? 'red' : undefined}>
          {title}
        </Title>
      </Group>
      <Stack gap="xs">{children}</Stack>
    </Card>
  );
};

// --- Compound Component Export ---

export const ActionCard = Object.assign(ActionCardRoot, {
  Action: ActionCardAction,
  Button: ActionCardButton,
  Divider: ActionCardDivider,
  Description: ActionCardDescription,
  Empty: ActionCardEmpty,
});

// Also export individual components
export {
  ActionCardAction,
  ActionCardButton,
  ActionCardDescription,
  ActionCardDivider,
  ActionCardEmpty,
};
